/**
 * The composition's form, described as data.
 *
 * A composition is many blocks; its user-facing form is the union of
 * their slots. This projects that union into something a host can render
 * without knowing anything about blocks: ordered groups (one per block
 * instance with fillable slots), one field per distinct slot key, and the
 * exact visibility condition for each field.
 *
 * Two rules from `compileComposition` shape the projection:
 *
 *  - A block reads `{ ...composition.values, ...instance.input }`, so a key
 *    the composition fixes in `instance.input` is not the user's to fill
 *    for that instance — it is left out of the form.
 *  - A slot key read by several blocks is one answer, so it is one field,
 *    owned by the first group that reads it.
 */
import { evaluateCondition, isSlotRequired } from '../block/slots.js';
import { applyAutofill, readManualKeys } from '../block/autofill.js';
const FALSE_CONDITION = { any: [] };
/**
 * Partially evaluates a condition: every leaf whose key is in `fixed` is
 * decided now; the rest stays as data. Exact, because `fixed` keys shadow
 * the shared values in the block's scope.
 */
export function bindCondition(condition, fixed) {
    if (!condition)
        return true;
    if ('all' in condition) {
        const parts = condition.all.map((c) => bindCondition(c, fixed));
        if (parts.includes(false))
            return false;
        const rest = parts.filter((c) => c !== true);
        return rest.length === 0 ? true : rest.length === 1 ? rest[0] : { all: rest };
    }
    if ('any' in condition) {
        if (condition.any.length === 0)
            return false;
        const parts = condition.any.map((c) => bindCondition(c, fixed));
        if (parts.includes(true))
            return true;
        const rest = parts.filter((c) => c !== false);
        return rest.length === 0 ? false : rest.length === 1 ? rest[0] : { any: rest };
    }
    if ('not' in condition) {
        const inner = bindCondition(condition.not, fixed);
        return typeof inner === 'boolean' ? !inner : { not: inner };
    }
    if (Object.prototype.hasOwnProperty.call(fixed, condition.key))
        return evaluateCondition(condition, fixed);
    return condition;
}
const andBound = (a, b) => {
    if (a === false || b === false)
        return false;
    if (a === true)
        return b;
    if (b === true)
        return a;
    return { all: [a, b] };
};
const orBound = (items) => {
    if (items.includes(true))
        return true;
    const rest = items.filter((c) => c !== false);
    return rest.length === 0 ? false : rest.length === 1 ? rest[0] : { any: rest };
};
const toCondition = (b) => b === true ? undefined : b === false ? FALSE_CONDITION : b;
const fromCondition = (c) => c ?? true;
export function describeCompositionForm(composition, registry) {
    const diagnostics = [];
    const groups = [];
    const fields = new Map();
    const defaultPart = composition.parts?.[0]?.key ?? 'main';
    for (const instance of composition.blocks) {
        const definition = registry.resolve(instance.blockRef);
        if (!definition) {
            diagnostics.push({
                severity: 'error',
                code: 'unknown_block',
                message: `No registered block "${instance.blockRef}".`,
                instanceId: instance.instanceId,
                blockRef: instance.blockRef,
            });
            continue;
        }
        const fixed = instance.input ?? {};
        const include = bindCondition(instance.includeWhen, fixed);
        const group = {
            instanceId: instance.instanceId,
            blockRef: instance.blockRef,
            blockKey: definition.blockKey,
            title: definition.title,
            summary: definition.summary,
            part: instance.part ?? defaultPart,
            placement: instance.slot ?? 'body',
            ...(include === true ? {} : { condition: toCondition(include) }),
            fields: [],
            sharedKeys: [],
            fixed: { ...fixed },
        };
        for (const slot of definition.slots) {
            if (Object.prototype.hasOwnProperty.call(fixed, slot.key))
                continue;
            const visible = bindCondition(slot.visibleWhen, fixed);
            const bound = andBound(include, visible);
            const occurrence = {
                instanceId: instance.instanceId,
                ...(visible === true ? {} : { condition: toCondition(visible) }),
            };
            const requiredWhen = andBound(include, andBound(visible, bindCondition(slot.requiredWhen ?? FALSE_CONDITION, fixed)));
            const existing = fields.get(slot.key);
            if (existing) {
                existing.occurrences.push(occurrence);
                existing._bounds.push(bound);
                existing.required ||= Boolean(slot.required);
                existing.statutory ||= Boolean(slot.statutory);
                if (!existing.autofill && slot.autofill)
                    existing.autofill = slot.autofill;
                if (existing.default === undefined && slot.default !== undefined)
                    existing.default = slot.default;
                if (!existing.derivedFrom && slot.derivedFrom)
                    existing.derivedFrom = slot.derivedFrom;
                if (slot.derivedFromParent)
                    existing.derivedFromParent = true;
                if (requiredWhen !== false)
                    existing._requiredWhen.push(requiredWhen);
                if (!group.sharedKeys.includes(slot.key) && existing.groupId !== group.instanceId) {
                    group.sharedKeys.push(slot.key);
                }
                continue;
            }
            const field = {
                key: slot.key,
                label: slot.label,
                type: slot.type,
                ...(slot.options ? { options: slot.options.map((o) => ({ ...o })) } : {}),
                required: Boolean(slot.required),
                statutory: Boolean(slot.statutory),
                ...(slot.help ? { help: slot.help } : {}),
                ...(slot.autofill ? { autofill: slot.autofill } : {}),
                ...(slot.repeats ? { repeats: true } : {}),
                ...(slot.columns ? { columns: slot.columns } : {}),
                ...(slot.default !== undefined ? { default: slot.default } : {}),
                ...(slot.min !== undefined ? { min: slot.min } : {}),
                ...(slot.max !== undefined ? { max: slot.max } : {}),
                ...(slot.integer ? { integer: true } : {}),
                ...(slot.derivedFrom ? { derivedFrom: slot.derivedFrom } : {}),
                ...(slot.derivedFromParent ? { derivedFromParent: true } : {}),
                groupId: group.instanceId,
                occurrences: [occurrence],
                _bounds: [bound],
                _requiredWhen: requiredWhen === false ? [] : [requiredWhen],
            };
            fields.set(slot.key, field);
            group.fields.push(field);
        }
        if (group.fields.length > 0 || group.sharedKeys.length > 0)
            groups.push(group);
    }
    for (const field of fields.values()) {
        const combined = toCondition(orBound(field._bounds));
        if (combined)
            field.condition = combined;
        if (!field.required && !field.statutory && field._requiredWhen.length > 0) {
            const when = orBound(field._requiredWhen);
            if (when === true)
                field.required = true;
            else if (when !== false)
                field.requiredWhen = when;
        }
        delete field._bounds;
        delete field._requiredWhen;
    }
    return {
        documentKey: composition.documentKey,
        documentVersion: composition.documentVersion,
        title: composition.title,
        groups,
        fields: groups.flatMap((g) => g.fields),
        partySlots: composition.meta?.partySlots ?? [],
        propertySlots: composition.meta?.propertySlots ?? [],
        defaults: { ...(composition.values ?? {}) },
        diagnostics,
    };
}
const isEmpty = (value) => value == null || value === '' || (Array.isArray(value) && value.length === 0);
/** Which fields apply for these values, and which mandatory ones are still empty. */
export function computeFormState(form, values) {
    const scope = { ...form.defaults, ...values };
    const index = new Map(form.fields.map((f) => [f.key, f]));
    const visible = form.fields.filter((f) => evaluateCondition(f.condition, scope, index));
    const missing = visible.filter((f) => isSlotRequired(f, scope, index) && isEmpty(scope[f.key]));
    const visibleSet = new Set(visible.map((f) => f.key));
    return {
        visible,
        visibleKeys: visible.map((f) => f.key),
        visibleGroups: form.groups
            .filter((g) => evaluateCondition(g.condition, scope, index) &&
            (g.fields.some((f) => visibleSet.has(f.key)) || g.sharedKeys.some((k) => visibleSet.has(k))))
            .map((g) => g.instanceId),
        missing,
        missingStatutory: missing.filter((f) => f.statutory),
    };
}
/**
 * Fills every autofill-bound field that is in auto mode from the given
 * party/property literals; manual fields keep the user's text. Pure — the
 * Regta wrap maps its rows to literals and calls this.
 */
export function resolveAutofill(input) {
    const fields = 'form' in input
        ? input.form.fields
        : 'composition' in input
            ? describeCompositionForm(input.composition, input.registry).fields
            : input.slots;
    const result = applyAutofill({
        fields,
        values: input.values,
        ...(input.parties ? { parties: input.parties } : {}),
        ...(input.properties ? { properties: input.properties } : {}),
        ...(input.manual ? { manual: input.manual } : {}),
        ...(input.missing ? { missing: input.missing } : {}),
    });
    if (!input.parentValues)
        return result;
    const parent = input.parentValues;
    const manual = input.manual ? new Set(input.manual) : readManualKeys(input.values);
    const seen = new Set([...result.autoKeys, ...result.manualKeys]);
    for (const field of fields) {
        if (!field.derivedFrom && !field.derivedFromParent)
            continue;
        if (seen.has(field.key))
            continue;
        seen.add(field.key);
        if (manual.has(field.key)) {
            result.manualKeys.push(field.key);
            continue;
        }
        result.autoKeys.push(field.key);
        const from = field.derivedFrom ?? field.key;
        if (!Object.prototype.hasOwnProperty.call(parent, from))
            continue;
        const next = parent[from];
        if (result.values[field.key] !== next)
            result.changedKeys.push(field.key);
        result.values[field.key] = next;
    }
    return result;
}
/**
 * Create-time seeding: every field with a `default` whose key is absent
 * from `values` gets it (cloned). Present keys — including an explicit
 * `null` — are never overwritten. Pure.
 */
export function applyDefaults(form, values = {}) {
    const out = { ...values };
    for (const field of form.fields) {
        if (field.default === undefined || field.type === 'note')
            continue;
        if (out[field.key] !== undefined)
            continue;
        out[field.key] = typeof field.default === 'object' && field.default !== null ? structuredClone(field.default) : field.default;
    }
    return out;
}
/** Fields that would be filled from a source — for a host's "auto/manual" toggles. */
export const autofillFields = (form) => form.fields.filter((f) => f.autofill);
//# sourceMappingURL=form.js.map